<!-- ImportLabResultsHL7File.vue -->
<script lang="ts" setup>
import { computed, reactive, ref } from 'vue';
import TextFileSelectButton from '../../../components/Buttons/TextFileSelectButton.vue';
import Title from '../../../components/Section/Title.vue';
import { TitleType, ButtonDisplayStyle, TitleDisplayStyle, DisplayStyle } from '../../../Enums/UiEnums';
import { ITextFileAndContents } from '../../../lib/FileUtils';
import PrettyFileSize from '../../../components/PrettyFileSize.vue';
import MessageBadge from '../../../components/Messages/MessageBadge.vue';
import { confirmDialog, ModalStyles, popAlert, showToast } from '../../../lib/Notification';
import ActionButtonV2 from '../../../components/ActionButtonV2.vue';
import Row from '../../../components/Section/Row.vue';
import ChoiceButton from '../../../components/Buttons/ChoiceButton.vue';
import { ActionButtonStyle } from '../../../lib/Types/Types';
import LoadingSection from '../../../components/LoadingSection.vue';
import { ChoiceApi } from '../../../LibMain';
import { closeModal } from '../../../lib/Legacy/ModalWindow';
import { LegacyErrorResult } from '../../../lib/ChoiceApi';
import { HL7Data } from '../../../lib/HL7/HL7DataHelpers';
import AlertSection from '../../../components/Messages/AlertSection.vue';
import PrettyDate from '../../../components/PrettyDate.vue';
import MessageText from '../../../components/Messages/MessageText.vue';
import BulletedList from '../../../components/Formatting/BulletedList.vue';


    const compData = reactive<{
        hl7ValidationIssue?: string[],
        hl7OrderResultValidationIssue?: string
        selectedFile?: ITextFileAndContents,
        isValidHL7File?: boolean,
        uploadingHL7File: boolean,
        importedFileList: { fileName: string, timestamp: Date }[]
    }>({
        uploadingHL7File: false,
        importedFileList: []
    });


    // Handle the file selection
    async function fileSelected(selectedFile: ITextFileAndContents)
    {
        compData.selectedFile = undefined;
        if (!selectedFile.contents)
        {
            return;
        }

        compData.hl7OrderResultValidationIssue = undefined;
        compData.hl7ValidationIssue = undefined;

        // Make sure we have a valid HL7 file
        const hl7Data = new HL7Data(selectedFile.contents, true);
        compData.selectedFile = selectedFile;
        compData.isValidHL7File = hl7Data.isValid;

        // Make sure we have a valid HL7 file
        if (!compData.isValidHL7File)
        {
            compData.hl7ValidationIssue = hl7Data.getHl7ValidationIssue();

            await popAlert("The selected file does not appear to be a valid HL7 file", "Cannot validate file");
            return;
        }

        // Make sure it looks like we have a valid Results file
        compData.hl7OrderResultValidationIssue = hl7Data.getOrderResultValidationIssue();
        if (undefined !== compData.hl7OrderResultValidationIssue)    
        {
            await popAlert("The selected file may or may not be a valid HL7 result file.", "Cannot validate file");
        }
    };

    async function uploadFile()
    {
        // If the file is empty, don't import it
        if (!compData.selectedFile?.contents)
        {
            await popAlert("File lacks contents");
            return;
        }

        // If the file is not valid, ask if they want to force it into the import queue
        if (!compData.isValidHL7File)
        {
            const resp = await confirmDialog(
                "The file does not appear to contain valid HL7 data.  Do you want to force the file into the import queue anyway?",
                {
                    ConfirmText: "Force Import",
                    RejectText: "Cancel",
                    titleHTML: "Invalid HL7 File",
                    modalStyle: ModalStyles.danger
                });
            if(!resp)
            {
                return;
            }                
        }

        const resp = await uploadFileApiCall(compData.selectedFile.contents);
        if (!resp || resp.error)
        {
            await popAlert(
                resp?.error_message ?? "An error occurred while importing the HL7 file.",
                "Import Error", ModalStyles.danger);
            return;
        }

        compData.importedFileList.push({
            fileName: compData.selectedFile?.file.name ?? "Unknown File",
            timestamp: new Date()
        });

        // There isn't isn't a parent, show an alert before closing
        const showingInModalDialog = (parent != self);
        if (showingInModalDialog)
        {
            showToast(`Successfully added file to import queue: ${compData.selectedFile?.file.name}.`, DisplayStyle.Success);
        }
        else
        {
            await popAlert("Successfully added file to import queue: " + compData.selectedFile?.file.name + ".", "Import Queued");
        }        

        if (showingInModalDialog)
        {
            // Would they like to refresh the lab order list
            let refreshList = false;
            if (await confirmDialog("Would you like to refresh the lab orders list to show items imported today?"))
            {
                refreshList = true;
            }
    
            closeModal(true, undefined, { refreshList: refreshList });
        }

        reset();
    }

    async function uploadFileApiCall(hl7Data: string): Promise<LegacyErrorResult|undefined>
    {
        try
        {
            compData.uploadingHL7File = true;
            const resp = await ChoiceApi.LabOrder.importResultsHL7(hl7Data);
            compData.uploadingHL7File = false;
            return resp;
        }
        catch(e)
        {
            return {error: -1, error_message: (<Error>e).message};
        }        
    }

    function reset()
    {
        compData.selectedFile = undefined        
    }

    const isValidOrderResultFile = computed<boolean>(() => undefined === compData.hl7OrderResultValidationIssue && undefined === compData.hl7ValidationIssue);

</script>
<template>
    <Title :type="TitleType.PageTitle">
        Import HL7 Lab Results File
    </Title>
    <br/>

    <Row v-if="!compData.selectedFile">
        <TextFileSelectButton label="Choose File" accept=".hl7,.txt" @fileSelected="fileSelected" />
        <Title :type="TitleType.SectionTitle" :display="TitleDisplayStyle.Inline">
            Select a valid HL7 File
        </Title>
    </Row>

    <LoadingSection v-if="compData.selectedFile" loadingMessage="Uploading HL7 Lab Results..." :isLoading="compData.uploadingHL7File">
        <Row>
            {{ compData.selectedFile.file.name }}
            <MessageBadge>
                <PrettyFileSize :bytes="compData.selectedFile.file.size" />
            </MessageBadge>

            <AlertSection v-if="!compData.isValidHL7File" :displayStyle="DisplayStyle.Danger">
                The selected file does not appear to be a valid HL7 file.
                <BulletedList :items="compData.hl7ValidationIssue" />
            </AlertSection>

            <AlertSection v-if="compData.isValidHL7File && !isValidOrderResultFile" :displayStyle="DisplayStyle.Warning" :marginTop="1">
                The selected file may or may not be a valid HL7 result file. You can still submit it and see if it can be processed.
                <BulletedList :items="compData.hl7OrderResultValidationIssue" />
            </AlertSection>

            <AlertSection v-if="true == isValidOrderResultFile" :displayStyle="DisplayStyle.Success" :marginTop="1">
                The selected appears to be a valid HL7 result file.
            </AlertSection>

        </Row>

        <Row>
            <ActionButtonV2
                :action="ActionButtonStyle.Import"
                :displayStyle="compData.isValidHL7File ? DisplayStyle.Primary : DisplayStyle.Danger"
                text="Importing"
                :performAction='uploadFile'>
                Import File
            </ActionButtonV2>
            <ChoiceButton 
                :action="ActionButtonStyle.Refresh"
                @click="reset" 
                :marginStart="1" 
                :displayStyle="ButtonDisplayStyle.OutlinePrimary" 
                title="Clear the selected file and start over">
                Reset
            </ChoiceButton>
        </Row>
    </LoadingSection>

    <AlertSection v-if="compData.importedFileList?.length" 
        :displayStyle="DisplayStyle.Success" 
        :marginTop="2">
        <Title :type="TitleType.PageTitle">
            Queued Files To Import
        </Title>
        <Row :bottomPadding="1">
            <MessageText :displayStyle="DisplayStyle.Light">
                Files will be processed in the background.  Data may not be available immediately.
            </MessageText>
        </Row>

        <div v-for="importedItem in compData.importedFileList">
            {{ importedItem.fileName }} 
            <MessageBadge :displayStyle="DisplayStyle.Light">
                <PrettyDate :date="importedItem.timestamp" :showTime="true" />
            </MessageBadge>
        </div>

    </AlertSection>
</template>